export interface PrimaryRouteWarmTarget {
  id: 'home' | 'discovery' | 'market' | 'community' | 'profile';
  to: '/home' | '/discovery' | '/market' | '/community' | '/profile';
}

export interface RoutePreloader {
  preloadRoute: (options: { to: PrimaryRouteWarmTarget['to'] }) => Promise<unknown>;
}

export interface WarmStartResult {
  attempted: number;
  fulfilled: number;
  failed: number;
}

export interface WarmStartConnection {
  online: boolean;
  saveData?: boolean;
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
}

export const PRIMARY_ROUTE_WARM_TARGETS: readonly PrimaryRouteWarmTarget[] = [
  { id: 'home', to: '/home' },
  { id: 'discovery', to: '/discovery' },
  { id: 'market', to: '/market' },
  { id: 'community', to: '/community' },
  { id: 'profile', to: '/profile' },
];

const WARM_CONCURRENCY = 2;

// ponytail: "likely next" is a nav-adjacency heuristic (no analytics yet). Warm the
// primary tabs immediately adjacent to the active one rather than every route, so
// bootstrap warming primes the cheapest likely destinations. Replace with an
// analytics-derived ordering when destination-frequency data is available.
export const LIKELY_NEXT_WARM_LIMIT = 2;

export function getLikelyNextWarmTargets(activePathname: string): PrimaryRouteWarmTarget[] {
  const activeIndex = PRIMARY_ROUTE_WARM_TARGETS.findIndex(
    (target) => target.to === activePathname
  );
  const candidates = PRIMARY_ROUTE_WARM_TARGETS.filter((target) => target.to !== activePathname);

  if (activeIndex === -1) return candidates.slice(0, LIKELY_NEXT_WARM_LIMIT);

  return candidates
    .slice()
    .sort(
      (a, b) =>
        Math.abs(PRIMARY_ROUTE_WARM_TARGETS.indexOf(a) - activeIndex) -
        Math.abs(PRIMARY_ROUTE_WARM_TARGETS.indexOf(b) - activeIndex)
    )
    .slice(0, LIKELY_NEXT_WARM_LIMIT);
}

export function shouldSkipPrimaryRouteWarming(connection: WarmStartConnection): boolean {
  return (
    !connection.online ||
    connection.saveData === true ||
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    (connection.rtt !== undefined && connection.rtt > 400) ||
    (connection.downlink !== undefined && connection.downlink < 1)
  );
}

/**
 * Preloads the given primary routes (capped at WARM_CONCURRENCY in flight) without
 * letting an optional warm-up failure delay application bootstrap. Callers pass a
 * small likely-next set (see getLikelyNextWarmTargets) so warming stays off the
 * first-paint path rather than priming every route up front.
 */
export async function warmPrimaryRoutes(
  router: RoutePreloader,
  targets: readonly PrimaryRouteWarmTarget[]
): Promise<WarmStartResult> {
  let nextTargetIndex = 0;
  let fulfilled = 0;
  let failed = 0;

  const worker = async () => {
    while (nextTargetIndex < targets.length) {
      const target = targets[nextTargetIndex++];
      if (!target) return;

      try {
        await router.preloadRoute({ to: target.to });
        fulfilled += 1;
      } catch {
        failed += 1;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(WARM_CONCURRENCY, targets.length) }, worker));

  return { attempted: targets.length, fulfilled, failed };
}
